import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { IEventMetadata, ISourceData, ISourceDataConfiguration, ISourceDataConfigurationEvent, ISourceDataEvent, ITrialFormMetadata } from '@/api/dtos';
import { BasicAlert, CollapsiblePanel, DefaultCollapsiblePanelComponent, ICollapsiblePanelComponentProps, Markdown } from '@ngt/forms';
import { alpha, Box, Button, Chip, Divider, styled, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faRepeat } from '@fortawesome/pro-duotone-svg-icons';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/duotone-light-svg-icons';
import { ISourceDataFormEventComponentProps } from '@/utilities/forms/SourceDataFormProcessor';
import SourceDataExpandingContext from '@/contexts/forms/SourceDataExpandingContext';
import useSourceDataEventReviewCount from '@/hooks/form/useSourceDataEventReviewCount';

const CollapsiblePanelTitle = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    justifyContent: 'flex-start',
    borderRadius: 0,
    minHeight: 74
}));

const IconContainer = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(2),
    display: 'inline-block'
}));

const TitleChip = styled(Chip)(({ theme }) => ({
    marginLeft: 'auto'
}));

const RepeatChip = styled(Chip)(({ auto, theme }: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(1)
}));

const CountChip = styled(Chip)(({ auto, theme }: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(1),
    minWidth: 110
}));

const CountFlex = styled(Box)(({ auto, theme }: any) => ({
    display: 'flex'
}));

const Count = styled(Box)(({ auto, theme }: any) => ({
    minWidth: 50
}));

const Border = styled(Box)(({ theme }) => ({
    borderLeft: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`
}));

const Content = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(8),
    borderLeft: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`
}));

const SourceDataFormEventExpanding: FC<ISourceDataFormEventComponentProps> = ({
    settings,
    children,
    ...event
}) => {
    const {
        eventLabel,
        eventMetadata,
        eventSourceData,
        eventRepeat,
        eventIdentifier,
        eventPropertyPath,
        forms,
        eventConfiguration,
    } = event;

    const hasEvent = !!eventSourceData;

    const noForms = useMemo(() => forms?.filter(x => x.formEnabled && x.formSourceData).length === 0, [forms]);

    const { expanded, toggleExpanding } = useContext(SourceDataExpandingContext);
    
    const counts = useSourceDataEventReviewCount(eventPropertyPath!, event);

    return (
        <>
            <CollapsiblePanelTitle 
                variant='contained' 
                color="secondary"
                fullWidth
                onClick={() => toggleExpanding(eventIdentifier!)}
                disabled={!hasEvent || noForms}
            >
                <Typography variant="h2">
                    <IconContainer>
                        <FontAwesomeIcon icon={faCalendarDays} />
                    </IconContainer>
                    {eventLabel}
                </Typography>
                {
                    !hasEvent && (
                        <TitleChip
                            label="No Recorded Event"
                        />
                    )
                }
                {
                    hasEvent && noForms && (
                        <RepeatChip
                            // @ts-ignore
                            auto={hasEvent ? "1" : undefined}
                            label="No Recorded Forms"
                        />
                    )
                }
                {
                    eventMetadata?.repeating && (
                        <Tooltip title="Repeat">
                            <RepeatChip
                                // @ts-ignore
                                auto={hasEvent && !noForms ? "1" : undefined}
                                icon={<FontAwesomeIcon icon={faRepeat} />}
                                label={eventRepeat}
                            />
                        </Tooltip>
                    )
                }
                {
                    counts && (
                        <Tooltip title="Fields Pending Review">
                            <CountChip
                                // @ts-ignore
                                auto={hasEvent && !eventMetadata?.repeating ? "1" : undefined}
                                label={
                                    <CountFlex>
                                        <Count>
                                            {counts.reviewed}
                                        </Count>
                                        /
                                        <Count>
                                            {counts.total}
                                        </Count>
                                    </CountFlex>
                                }
                                color={counts.reviewed !== counts.total ? 'error' : 'success'}
                            />
                        </Tooltip>
                    )
                }
            </CollapsiblePanelTitle>
            {
                expanded[eventIdentifier!] && (
                    <>
                        { 
                            !!eventConfiguration?.alertInformation && (
                                <BasicAlert
                                    description={<Markdown.Preview>{eventConfiguration.alertInformation}</Markdown.Preview>}
                                    severity="info"
                                    icon={faInfoCircle}
                                />
                            )
                        }
                        { 
                            !!eventConfiguration?.alertWarning && (
                                <BasicAlert
                                    description={<Markdown.Preview>{eventConfiguration.alertWarning}</Markdown.Preview>}
                                    severity="warning"
                                    icon={faExclamationCircle}
                                />
                            )
                        }
                        { 
                            !!eventConfiguration?.alertError && (
                                <BasicAlert
                                    description={<Markdown.Preview>{eventConfiguration.alertError}</Markdown.Preview>}
                                    severity="error"
                                    icon={faExclamationTriangle}
                                />
                            )
                        }
                        { 
                            !!eventConfiguration?.alertSuccess && (
                                <BasicAlert
                                    description={<Markdown.Preview>{eventConfiguration.alertSuccess}</Markdown.Preview>}
                                    severity="success"
                                    icon={faCheckCircle}
                                />
                            )
                        }
                    </>
                )
            }
            <Divider />
        </>
    );
}

export default SourceDataFormEventExpanding;